import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useEntitlements } from "@/context/EntitlementsContext";

const POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

/**
 * Post-payment landing page — polls /api/payments/status until the server
 * confirms the payment, then re-fetches entitlements so unlocked features
 * appear immediately.
 */
export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useEntitlements();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | expired | failed
  const [record, setRecord] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    let cancel = false;
    const tick = async () => {
      if (cancel) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setRecord(data);
        if (data.payment_status === "paid") {
          setState("paid");
          try { await refresh(); } catch { /* non-fatal */ }
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
      } catch { /* ignore transient */ }
      if (attempts >= POLL_ATTEMPTS) { setState("expired"); return; }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => { cancel = true; };
  }, [sessionId, refresh]);

  return (
    <div className="max-w-lg mx-auto py-16 text-center space-y-6" data-testid="payment-success-page">
      {state === "polling" && (
        <>
          <Loader2 size={40} className="animate-spin mx-auto text-[#FF5A1F]" />
          <h1 className="font-display text-3xl font-black tracking-tighter">Confirming your payment…</h1>
          <p className="text-sm text-[var(--text-muted)]">This usually takes just a few seconds.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 border border-emerald-500 flex items-center justify-center">
            <CheckCircle2 size={32} className="text-emerald-400" />
          </div>
          <div>
            <p className="ft-overline text-emerald-400">PAYMENT SUCCESSFUL</p>
            <h1 className="font-display text-4xl font-black tracking-tighter mt-1">You're on {record?.plan_id?.toUpperCase() || "your new plan"} 🎉</h1>
            <p className="mt-2 text-sm text-[var(--text-muted)]">
              Amount charged: <span className="text-white">₹{record?.amount ? new Intl.NumberFormat("en-IN").format(record.amount) : "—"}</span> · Billing: <span className="text-white">{record?.billing_cycle || "monthly"}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={() => nav("/profile?tab=subscription", { replace: true })}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#FF5A1F] hover:bg-[#E64A0F] text-white text-sm font-semibold"
            data-testid="payment-continue-btn"
          >
            <Sparkles size={14} /> See what's unlocked <ArrowRight size={14} />
          </button>
        </>
      )}
      {(state === "expired" || state === "failed") && (
        <>
          <div className="w-16 h-16 mx-auto rounded-full bg-yellow-500/20 border border-yellow-500 flex items-center justify-center">
            <XCircle size={32} className="text-yellow-400" />
          </div>
          <h1 className="font-display text-3xl font-black tracking-tighter">Still confirming your payment…</h1>
          <p className="text-sm text-[var(--text-muted)]">
            We couldn't confirm your payment in time. If you were charged, your subscription will activate automatically once Razorpay confirms it. Refresh Manage Subscription in a minute.
          </p>
          <button
            type="button"
            onClick={() => nav("/profile?tab=subscription", { replace: true })}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[var(--border-line)] text-sm hover:bg-[var(--hover)]"
            data-testid="payment-back-btn"
          >
            Back to Manage Subscription
          </button>
        </>
      )}
    </div>
  );
}
